'use client'

import React, { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import type { User } from '@supabase/supabase-js'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useCachedUser } from '@/hooks/useCachedUser'
import { useNotifications } from '@/hooks/useNotifications'
import { SlideOutPanel } from '@/components/ui/slide-out-panel'
import { WorkspaceForm } from '@/components/workspace-form'
import { WorkspaceSettingsPanel } from '@/components/WorkspaceSettingsPanel'
import { HugeiconsIcon } from '@hugeicons/react'
import {
  Settings01Icon as SettingsSolidIcon,
  Doctor01Icon as Doctor01SolidIcon,
  BinocularsIcon as BinocularsSolidIcon,
  Route01Icon as Route01SolidIcon,
  UserDollarIcon as UserDollarSolidIcon,
  TokenCircleIcon as TokenCircleSolidIcon,
  UserGroup03Icon as UserGroup03SolidIcon,
  Money01Icon as Money01SolidIcon,
  Wallet01Icon as Wallet01SolidIcon,
  Notification01Icon as Notification01SolidIcon,
  PinIcon,
  PinOffIcon,
} from '@hugeicons-pro/core-solid-rounded'
import {
  PlusSignIcon as PlusSignStrokeIcon,
  Tick02Icon,
  Notification01Icon as Notification01StrokeIcon,
  Clock01Icon as Clock01StrokeIcon,
  FlashIcon as FlashStrokeIcon,
  Settings01Icon as Settings01StrokeIcon,
  MoreHorizontalIcon,
  ArrowRight01Icon,
} from '@hugeicons-pro/core-stroke-rounded'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  DEFAULT_PINNED_SIDEBAR_CHANNELS,
  orderPinnedSidebarChannels,
  readPinnedSidebarChannels,
  writePinnedSidebarChannels,
  type SidebarChannelDef,
  type SidebarChannelId,
} from '@/lib/sidebar/pinned-channels'
import {
  DEFAULT_PINNED_SIDEBAR_TOOLS,
  orderPinnedSidebarTools,
  readPinnedSidebarTools,
  writePinnedSidebarTools,
  type SidebarToolDef,
  type SidebarToolId,
} from '@/lib/sidebar/pinned-tools'
import { AppSidebarWorkspaceMenu } from '@/components/AppSidebarWorkspaceMenu'
import type { SidebarRailMode } from '@/components/AppSidebarUserMenu'
import { useWorkspace, type Workspace } from '@/contexts/WorkspaceContext'
import { useIsDesktop } from '@/hooks/useIsDesktop'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Logo } from '@/components/Logo'
import { PendingNavProvider } from '@/contexts/PendingNavContext'
import NavLink from '@/components/NavLink'
import { getChannelIconSolid, getChannelIconStroke } from '@/lib/channels/icons'
import { useActiveBrand } from '@/hooks/useActiveBrand'
import { useChat } from '@/contexts/ChatContext'
import {
  Analytics01Icon as Analytics01SolidIcon,
  PlayCircleIcon as PlayCircleSolidIcon,
  Home03Icon as Home03SolidIcon,
  AiBrain05Icon as AiBrain05SolidIcon,
  FlashIcon as FlashSolidIcon,
  Clock01Icon as Clock01SolidIcon,
  WebDesign02Icon as WebDesign02SolidIcon,
  CodeCircleIcon as CodeCircleSolidIcon,
} from '@hugeicons-pro/core-solid-rounded'
import {
  Analytics01Icon as Analytics01StrokeIcon,
  PlayCircleIcon as PlayCircleStrokeIcon,
  Home03Icon as Home03StrokeIcon,
  BinocularsIcon as BinocularsStrokeIcon,
  Route01Icon as Route01StrokeIcon,
  UserDollarIcon as UserDollarStrokeIcon,
  TokenCircleIcon as TokenCircleStrokeIcon,
  Money01Icon as Money01StrokeIcon,
  Wallet01Icon as Wallet01StrokeIcon,
  WebDesign02Icon as WebDesign02StrokeIcon,
  AiBrain05Icon as AiBrain05StrokeIcon,
  Doctor01Icon as Doctor01StrokeIcon,
  UserGroup03Icon as UserGroup03StrokeIcon,
  CodeCircleIcon as CodeCircleStrokeIcon,
} from '@hugeicons-pro/core-stroke-rounded'

type WorkspaceDrawerView = 'list' | 'create'

type SidebarRailContentProps = {
  expanded: boolean
  onOpenWorkspaceSettings: (section?: string) => void
  userLoading: boolean
  user: User | null
  currentWorkspace: Workspace | null
  workspaces: Workspace[]
  workspaceRoles: Record<string, string>
  workspacesLoading: boolean
  workspaceMenuOpen: boolean
  setWorkspaceMenuOpen: (open: boolean) => void
  handleWorkspaceChange: (id: string) => void
  isDesktop: boolean
  setShowCreateForm: (open: boolean) => void
  setWorkspaceDrawerView: (v: WorkspaceDrawerView) => void
  setWorkspacePopoverOpenMobile: (open: boolean) => void
  logoHref: string
  overlayMode?: boolean
}

const SIDEBAR_RAIL_MODE_STORAGE_KEY = 'monarch.sidebarRailMode'
const SIDEBAR_RAIL_MODE_COOKIE_KEY = 'monarch.sidebarRailMode'
export const APP_SIDEBAR_EXPANDED_WIDTH = 216

function readSidebarRailMode(): SidebarRailMode | null {
  if (typeof window === 'undefined') return null
  try {
    const cookieRaw = document.cookie
      .split('; ')
      .find((entry) => entry.startsWith(`${SIDEBAR_RAIL_MODE_COOKIE_KEY}=`))
      ?.split('=')
      .slice(1)
      .join('=')
    const cookieValue = cookieRaw ? decodeURIComponent(cookieRaw) : null
    if (cookieValue === 'expanded' || cookieValue === 'collapsed') return cookieValue

    const raw = localStorage.getItem(SIDEBAR_RAIL_MODE_STORAGE_KEY)
    if (raw === 'expanded' || raw === 'collapsed') return raw
    if (raw === 'hover') {
      try {
        localStorage.setItem(SIDEBAR_RAIL_MODE_STORAGE_KEY, 'collapsed')
      } catch {
        /* ignore */
      }
      return 'collapsed'
    }
  } catch {
    /* ignore */
  }
  return null
}

function SidebarNavGroup({
  label,
  expanded,
  children,
}: {
  label?: string
  expanded: boolean
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {label ? (
        <div
          aria-hidden={!expanded || undefined}
          className={cn(
            'h-9 px-3 pb-0.5 flex items-end justify-start text-[13px] font-medium tracking-wider text-[#787881] dark:text-[#787881] transition-opacity duration-200',
            expanded ? 'opacity-100' : 'opacity-0'
          )}
        >
          {label}
        </div>
      ) : null}
      {children}
    </div>
  )
}

function SidebarToolPinButton({
  pinned,
  onToggle,
}: {
  pinned: boolean
  onToggle: (e: React.MouseEvent) => void
}) {
  return (
    <button
      type="button"
      onPointerDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle(e)
      }}
      aria-label={pinned ? 'Unpin from sidebar' : 'Pin to sidebar'}
      className="flex size-7 shrink-0 items-center justify-center rounded-md text-atrium-navlink-icon opacity-0 transition-opacity group-hover:opacity-100 hover:bg-atrium-navlink-active hover:text-atrium-black"
    >
      <HugeiconsIcon icon={pinned ? PinOffIcon : PinIcon} className="size-3.5" />
    </button>
  )
}

type SidebarToolRenderConfig = SidebarToolDef & {
  icon: React.ReactElement
  activeIcon: React.ReactElement
  badge?: React.ReactNode
}

function SidebarPinnedToolsGroup({
  expanded,
  overlayMode,
  tools,
  alertsBadge,
}: {
  expanded: boolean
  overlayMode?: boolean
  tools: SidebarToolRenderConfig[]
  alertsBadge?: React.ReactNode
}) {
  const pathname = usePathname() ?? ''
  const [pinnedToolIds, setPinnedToolIds] = useState<SidebarToolId[]>(DEFAULT_PINNED_SIDEBAR_TOOLS)
  const [moreToolsOpen, setMoreToolsOpen] = useState(false)

  useEffect(() => {
    setPinnedToolIds(readPinnedSidebarTools())
  }, [])

  const togglePin = useCallback((id: SidebarToolId) => {
    setPinnedToolIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((toolId) => toolId !== id)
        : orderPinnedSidebarTools([...prev, id])
      writePinnedSidebarTools(next)
      return next
    })
  }, [])

  const pinnedSet = useMemo(() => new Set(pinnedToolIds), [pinnedToolIds])
  const pinnedTools = useMemo(
    () => tools.filter((tool) => pinnedSet.has(tool.id)),
    [pinnedSet, tools]
  )
  const unpinnedTools = useMemo(
    () => tools.filter((tool) => !pinnedSet.has(tool.id)),
    [pinnedSet, tools]
  )

  const renderToolIcon = (tool: SidebarToolRenderConfig, active: boolean, size = 18) => {
    const iconEl = active ? tool.activeIcon : tool.icon
    return React.cloneElement(iconEl, {
      size,
      ...(active ? {} : { strokeWidth: 2 }),
      className: cn(
        'shrink-0',
        active ? 'text-atrium-black' : 'text-atrium-navlink-icon',
        iconEl.props.className
      ),
    })
  }

  const isToolActive = (tool: SidebarToolRenderConfig) =>
    tool.activeMatch
      ? tool.activeMatch(pathname)
      : pathname === tool.href || pathname.startsWith(`${tool.href}/`)

  const moreToolsActive = unpinnedTools.some((tool) => isToolActive(tool))

  const moreToolsTrigger = (
    <button
      type="button"
      aria-expanded={moreToolsOpen}
      className={cn(
        'group relative flex h-9 w-full cursor-inherit items-center justify-start rounded-lg text-[13px] font-medium transition-[padding,gap,background-color,color] duration-200 ease-out motion-reduce:transition-none',
        moreToolsOpen || moreToolsActive
          ? 'bg-atrium-navlink-active text-atrium-black'
          : 'bg-transparent text-atrium-navlink-text hover:bg-atrium-navlink-active active:text-atrium-black focus-visible:text-atrium-black'
      )}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center pl-[3px]">
        <HugeiconsIcon
          icon={MoreHorizontalIcon}
          size={18}
          strokeWidth={2}
          className={cn(
            'shrink-0',
            moreToolsOpen || moreToolsActive ? 'text-atrium-black' : 'text-atrium-navlink-icon'
          )}
        />
      </div>
      <span
        aria-hidden={!expanded || undefined}
        className={cn(
          'flex min-w-0 flex-1 items-center overflow-hidden transition-[max-width,opacity,transform] duration-200 ease-out motion-reduce:transition-none',
          expanded
            ? 'max-w-[min(256px,calc(100%-1.5rem))] translate-y-0 opacity-100'
            : 'pointer-events-none max-w-0 translate-y-1 opacity-0'
        )}
      >
        <span className="truncate">More tools</span>
      </span>
      {expanded ? (
        <HugeiconsIcon
          icon={ArrowRight01Icon}
          size={14}
          strokeWidth={2}
          className="mr-2 shrink-0 text-atrium-navlink-icon"
        />
      ) : null}
    </button>
  )

  return (
    <SidebarNavGroup label={pinnedTools.length > 0 ? 'Tools' : undefined} expanded={expanded}>
      {pinnedTools.map((tool) => (
        <NavLink
          key={tool.id}
          href={tool.href}
          variant="sidebar"
          iconOnly={!expanded}
          prefetch={overlayMode ? false : tool.prefetch ?? true}
          activeMatch={tool.activeMatch}
          icon={tool.icon}
          activeIcon={tool.activeIcon}
          badge={tool.id === 'alerts' ? alertsBadge : tool.badge}
          trailing={
            expanded ? (
              <SidebarToolPinButton pinned onToggle={() => togglePin(tool.id)} />
            ) : undefined
          }
        >
          {tool.label}
        </NavLink>
      ))}

      {unpinnedTools.length > 0 ? (
        <Popover open={moreToolsOpen} onOpenChange={setMoreToolsOpen}>
          {!expanded ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>{moreToolsTrigger}</PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">
                <span className="font-normal">More tools</span>
              </TooltipContent>
            </Tooltip>
          ) : (
            <PopoverTrigger asChild>{moreToolsTrigger}</PopoverTrigger>
          )}
          <PopoverContent
            className="z-[110] w-[min(calc(100vw-1.5rem),13.5rem)] rounded-xl border border-atrium-border bg-atrium-sidebar p-1.5 text-atrium-black shadow-lg"
            align="start"
            side="right"
            sideOffset={12}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex flex-col gap-0.5">
              {unpinnedTools.map((tool) => {
                const active = isToolActive(tool)
                return (
                  <div
                    key={tool.id}
                    className={cn(
                      'group flex h-9 items-center gap-0 rounded-lg px-0.5 text-[13px] font-medium transition-colors',
                      active
                        ? 'bg-atrium-navlink-active text-atrium-black'
                        : 'text-atrium-navlink-text hover:bg-atrium-navlink-active'
                    )}
                  >
                    <Link
                      href={tool.href}
                      onClick={() => setMoreToolsOpen(false)}
                      className="flex min-w-0 flex-1 items-center self-stretch outline-none"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center pl-[3px]">
                        {renderToolIcon(tool, active)}
                      </div>
                      <span className="min-w-0 flex-1 truncate">{tool.label}</span>
                      {tool.id === 'alerts' && alertsBadge ? (
                        <span className="shrink-0">{alertsBadge}</span>
                      ) : null}
                    </Link>
                    <SidebarToolPinButton pinned={false} onToggle={() => togglePin(tool.id)} />
                  </div>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </SidebarNavGroup>
  )
}

type SidebarChannelRenderConfig = SidebarChannelDef & {
  icon: React.ReactElement
  activeIcon: React.ReactElement
}

function SidebarPinnedChannelsGroup({
  expanded,
  overlayMode,
  channels,
}: {
  expanded: boolean
  overlayMode?: boolean
  channels: SidebarChannelRenderConfig[]
}) {
  const pathname = usePathname() ?? ''
  const [pinnedChannelIds, setPinnedChannelIds] = useState<SidebarChannelId[]>(
    DEFAULT_PINNED_SIDEBAR_CHANNELS
  )
  const [moreChannelsOpen, setMoreChannelsOpen] = useState(false)

  useEffect(() => {
    setPinnedChannelIds(readPinnedSidebarChannels())
  }, [])

  const togglePin = useCallback((id: SidebarChannelId) => {
    setPinnedChannelIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((channelId) => channelId !== id)
        : orderPinnedSidebarChannels([...prev, id])
      writePinnedSidebarChannels(next)
      return next
    })
  }, [])

  const pinnedSet = useMemo(() => new Set(pinnedChannelIds), [pinnedChannelIds])
  const pinnedChannels = useMemo(
    () => channels.filter((channel) => pinnedSet.has(channel.id)),
    [pinnedSet, channels]
  )
  const unpinnedChannels = useMemo(
    () => channels.filter((channel) => !pinnedSet.has(channel.id)),
    [pinnedSet, channels]
  )

  const renderChannelIcon = (channel: SidebarChannelRenderConfig, active: boolean, size = 18) => {
    const iconEl = active ? channel.activeIcon : channel.icon
    return React.cloneElement(iconEl, {
      size,
      ...(active ? {} : { strokeWidth: 2 }),
      className: cn('shrink-0', iconEl.props.className),
    })
  }

  const isChannelActive = (channel: SidebarChannelRenderConfig) =>
    channel.activeMatch
      ? channel.activeMatch(pathname)
      : pathname === channel.href || pathname.startsWith(`${channel.href}/`)

  const moreChannelsActive = unpinnedChannels.some((channel) => isChannelActive(channel))

  const moreChannelsTrigger = (
    <button
      type="button"
      aria-expanded={moreChannelsOpen}
      className={cn(
        'group relative flex h-9 w-full cursor-inherit items-center justify-start rounded-lg text-[13px] font-medium transition-[padding,gap,background-color,color] duration-200 ease-out motion-reduce:transition-none',
        moreChannelsOpen || moreChannelsActive
          ? 'bg-atrium-navlink-active text-atrium-black'
          : 'bg-transparent text-atrium-navlink-text hover:bg-atrium-navlink-active active:text-atrium-black focus-visible:text-atrium-black'
      )}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center pl-[3px]">
        <HugeiconsIcon
          icon={MoreHorizontalIcon}
          size={18}
          strokeWidth={2}
          className={cn(
            'shrink-0',
            moreChannelsOpen || moreChannelsActive ? 'text-atrium-black' : 'text-atrium-navlink-icon'
          )}
        />
      </div>
      <span
        aria-hidden={!expanded || undefined}
        className={cn(
          'flex min-w-0 flex-1 items-center overflow-hidden transition-[max-width,opacity,transform] duration-200 ease-out motion-reduce:transition-none',
          expanded
            ? 'max-w-[min(256px,calc(100%-1.5rem))] translate-y-0 opacity-100'
            : 'pointer-events-none max-w-0 translate-y-1 opacity-0'
        )}
      >
        <span className="truncate">More channels</span>
      </span>
      {expanded ? (
        <HugeiconsIcon
          icon={ArrowRight01Icon}
          size={14}
          strokeWidth={2}
          className="mr-2 shrink-0 text-atrium-navlink-icon"
        />
      ) : null}
    </button>
  )

  return (
    <SidebarNavGroup label={pinnedChannels.length > 0 ? 'Channels' : undefined} expanded={expanded}>
      {pinnedChannels.map((channel) => (
        <NavLink
          key={channel.id}
          href={channel.href}
          variant="sidebar"
          iconOnly={!expanded}
          prefetch={overlayMode ? false : channel.prefetch ?? true}
          activeMatch={channel.activeMatch}
          icon={channel.icon}
          activeIcon={channel.activeIcon}
          trailing={
            expanded ? (
              <SidebarToolPinButton pinned onToggle={() => togglePin(channel.id)} />
            ) : undefined
          }
        >
          {channel.label}
        </NavLink>
      ))}

      {unpinnedChannels.length > 0 ? (
        <Popover open={moreChannelsOpen} onOpenChange={setMoreChannelsOpen}>
          {!expanded ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <PopoverTrigger asChild>{moreChannelsTrigger}</PopoverTrigger>
              </TooltipTrigger>
              <TooltipContent side="right">
                <span className="font-normal">More channels</span>
              </TooltipContent>
            </Tooltip>
          ) : (
            <PopoverTrigger asChild>{moreChannelsTrigger}</PopoverTrigger>
          )}
          <PopoverContent
            className="z-[110] w-[min(calc(100vw-1.5rem),13.5rem)] rounded-xl border border-atrium-border bg-atrium-sidebar p-1.5 text-atrium-black shadow-lg"
            align="start"
            side="right"
            sideOffset={12}
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <div className="flex flex-col gap-0.5">
              {unpinnedChannels.map((channel) => {
                const active = isChannelActive(channel)
                return (
                  <div
                    key={channel.id}
                    className={cn(
                      'group flex h-9 items-center gap-0 rounded-lg px-0.5 text-[13px] font-medium transition-colors',
                      active
                        ? 'bg-atrium-navlink-active text-atrium-black'
                        : 'text-atrium-navlink-text hover:bg-atrium-navlink-active'
                    )}
                  >
                    <Link
                      href={channel.href}
                      onClick={() => setMoreChannelsOpen(false)}
                      className="flex min-w-0 flex-1 items-center self-stretch outline-none"
                    >
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center pl-[3px]">
                        {renderChannelIcon(channel, active)}
                      </div>
                      <span className="min-w-0 flex-1 truncate">{channel.label}</span>
                    </Link>
                    <SidebarToolPinButton pinned={false} onToggle={() => togglePin(channel.id)} />
                  </div>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      ) : null}
    </SidebarNavGroup>
  )
}

function SidebarRailContent({
  expanded,
  onOpenWorkspaceSettings,
  userLoading,
  user,
  currentWorkspace,
  workspaces,
  workspaceRoles,
  workspacesLoading,
  workspaceMenuOpen,
  setWorkspaceMenuOpen,
  handleWorkspaceChange,
  isDesktop,
  setShowCreateForm,
  setWorkspaceDrawerView,
  setWorkspacePopoverOpenMobile,
  logoHref,
  overlayMode,
}: SidebarRailContentProps) {
  // useActiveBrand still wired so other surfaces (e.g. brand-scoped tag
  // containers) can read the active brand. brandHref removed in Sherman
  // Phase 4 with the /brand route deletion.
  const { activeBrandId } = useActiveBrand(undefined, { enabled: !!user })
  void activeBrandId
  const { openChat } = useChat()
  // Unread count for Alerts nav badge.
  const {
    alertsUnreadCount,
    isLoading: notificationsLoading,
    refetch: refetchNotifications,
  } = useNotifications(user?.id)

  useEffect(() => {
    if (!user?.id || !currentWorkspace?.id) return
    void refetchNotifications()
  }, [currentWorkspace?.id, refetchNotifications, user?.id])

  const alertsBadge =
    !notificationsLoading && alertsUnreadCount > 0 ? (
      <span
        className="min-w-[16px] rounded-full bg-[#E0533D] px-1 py-0.5 text-center font-mono text-[9px] font-bold leading-none text-white"
        aria-hidden
      >
        {alertsUnreadCount > 99 ? '99+' : alertsUnreadCount}
      </span>
    ) : undefined

  const sidebarChannels: SidebarChannelRenderConfig[] = [
    {
      id: 'search',
      label: 'Search',
      href: '/channels/search',
      prefetch: true,
      activeMatch: (pathname) =>
        pathname === '/channels/search' || pathname.startsWith('/channels/search/'),
      icon: <HugeiconsIcon icon={getChannelIconStroke('search')!} className="text-ch-search" />,
      activeIcon: <HugeiconsIcon icon={getChannelIconSolid('search')!} className="text-ch-search" />,
    },
    {
      id: 'social',
      label: 'Social',
      href: '/channels/social',
      prefetch: true,
      activeMatch: (pathname) =>
        pathname === '/channels/social' || pathname.startsWith('/channels/social/'),
      icon: <HugeiconsIcon icon={UserGroup03StrokeIcon} className="text-ch-social" />,
      activeIcon: <HugeiconsIcon icon={UserGroup03SolidIcon} className="text-ch-social" />,
    },
    {
      id: 'tv',
      label: 'TV / CTV',
      href: '/channels/tv',
      prefetch: true,
      activeMatch: (pathname) => pathname === '/channels/tv' || pathname.startsWith('/channels/tv/'),
      icon: <HugeiconsIcon icon={getChannelIconStroke('tv')!} className="text-ch-tv" />,
      activeIcon: <HugeiconsIcon icon={getChannelIconSolid('tv')!} className="text-ch-tv" />,
    },
    {
      id: 'programmatic',
      label: 'Programmatic',
      href: '/channels/programmatic',
      prefetch: true,
      activeMatch: (pathname) =>
        pathname === '/channels/programmatic' || pathname.startsWith('/channels/programmatic/'),
      icon: (
        <HugeiconsIcon icon={getChannelIconStroke('programmatic')!} className="text-ch-programmatic" />
      ),
      activeIcon: (
        <HugeiconsIcon icon={getChannelIconSolid('programmatic')!} className="text-ch-programmatic" />
      ),
    },
    {
      id: 'audio',
      label: 'Audio',
      href: '/channels/audio',
      prefetch: true,
      activeMatch: (pathname) =>
        pathname === '/channels/audio' || pathname.startsWith('/channels/audio/'),
      icon: <HugeiconsIcon icon={getChannelIconStroke('audio')!} style={{ color: '#E0762D' }} />,
      activeIcon: <HugeiconsIcon icon={getChannelIconSolid('audio')!} style={{ color: '#E0762D' }} />,
    },
    {
      id: 'retail',
      label: 'Retail Media',
      href: '/channels/retail',
      prefetch: true,
      activeMatch: (pathname) =>
        pathname === '/channels/retail' || pathname.startsWith('/channels/retail/'),
      icon: <HugeiconsIcon icon={getChannelIconStroke('retail')!} style={{ color: '#C2185B' }} />,
      activeIcon: <HugeiconsIcon icon={getChannelIconSolid('retail')!} style={{ color: '#C2185B' }} />,
    },
  ]

  const sidebarTools: SidebarToolRenderConfig[] = [
    {
      id: 'budget',
      label: 'Budget',
      href: '/budget',
      prefetch: true,
      icon: <HugeiconsIcon icon={Money01StrokeIcon} />,
      activeIcon: <HugeiconsIcon icon={Money01SolidIcon} />,
    },
    {
      id: 'settings',
      label: 'Settings',
      href: '/settings',
      prefetch: true,
      icon: <HugeiconsIcon icon={Settings01StrokeIcon} />,
      activeIcon: <HugeiconsIcon icon={SettingsSolidIcon} />,
    },
    {
      id: 'alerts',
      label: 'Alerts',
      href: '/alerts',
      prefetch: true,
      activeMatch: (pathname) => pathname === '/alerts' || pathname.startsWith('/alerts/'),
      icon: <HugeiconsIcon icon={Notification01StrokeIcon} />,
      activeIcon: <HugeiconsIcon icon={Notification01SolidIcon} />,
    },
    {
      id: 'commands',
      label: 'Commands',
      href: '/commands',
      prefetch: true,
      activeMatch: (pathname) => pathname === '/commands' || pathname.startsWith('/commands/'),
      icon: <HugeiconsIcon icon={FlashStrokeIcon} />,
      activeIcon: <HugeiconsIcon icon={FlashSolidIcon} />,
    },
    {
      id: 'scheduled',
      label: 'Scheduled',
      href: '/scheduled',
      prefetch: true,
      activeMatch: (pathname) => pathname === '/scheduled' || pathname.startsWith('/scheduled/'),
      icon: <HugeiconsIcon icon={Clock01StrokeIcon} />,
      activeIcon: <HugeiconsIcon icon={Clock01SolidIcon} />,
    },
    {
      id: 'campaigns',
      label: 'Campaigns',
      href: '/campaigns',
      prefetch: true,
      activeMatch: (pathname) => pathname === '/campaigns' || pathname.startsWith('/campaigns/'),
      icon: <HugeiconsIcon icon={Analytics01StrokeIcon} />,
      activeIcon: <HugeiconsIcon icon={Analytics01SolidIcon} />,
    },
    {
      id: 'creatives',
      label: 'Creatives',
      href: '/creatives',
      prefetch: true,
      activeMatch: (pathname) =>
        pathname === '/creatives' ||
        pathname.startsWith('/creatives/') ||
        pathname === '/videos' ||
        pathname.startsWith('/videos/'),
      icon: <HugeiconsIcon icon={PlayCircleStrokeIcon} />,
      activeIcon: <HugeiconsIcon icon={PlayCircleSolidIcon} />,
    },
    {
      id: 'properties',
      label: 'Properties',
      href: '/properties',
      prefetch: undefined,
      activeMatch: (pathname) => pathname === '/properties' || pathname.startsWith('/properties/'),
      icon: <HugeiconsIcon icon={WebDesign02StrokeIcon} />,
      activeIcon: <HugeiconsIcon icon={WebDesign02SolidIcon} />,
    },
  ]

  const logoWordmark = (
    <button
      type="button"
      onClick={() => openChat()}
      aria-label="Open chat"
      className="flex items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-accent-600"
    >
      <Logo
        variant="wordmark"
        width={76}
        height={22}
        className="h-[22px] w-auto max-w-[136px] shrink-0 object-contain object-left transition-none"
      />
    </button>
  )

  const logoMark = (
    <button
      type="button"
      onClick={() => openChat()}
      aria-label="Open chat"
      className="flex items-center rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-accent-600"
    >
      <Logo
        variant="mark"
        width={24}
        height={24}
        className="size-7 shrink-0 rounded-lg transition-none"
      />
    </button>
  )

  return (
    <>
      <div className="flex h-12 w-full shrink-0 items-center justify-center gap-2 px-2">
        {expanded ? (
          <div className="flex min-w-0 flex-1 items-center justify-start overflow-hidden">
            <div className="ml-1 flex h-10 w-auto shrink-0 items-center justify-center">{logoWordmark}</div>
          </div>
        ) : (
          <div className="flex min-w-0 flex-1 items-center justify-center">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center">{logoMark}</div>
          </div>
        )}
      </div>

      {userLoading && !user ? (
        <AppSidebarWorkspaceMenu
          expanded={expanded}
          open={workspaceMenuOpen}
          onOpenChange={setWorkspaceMenuOpen}
          currentWorkspace={{ id: '', display_name: 'Acme, Inc.', avatar_path: null } as any}
          workspaces={[]}
          workspaceRoles={{}}
          workspacesLoading={true}
          handleWorkspaceChange={() => {}}
          isDesktop={isDesktop}
          setShowCreateForm={setShowCreateForm}
          setWorkspaceDrawerView={setWorkspaceDrawerView}
          setWorkspacePopoverOpenMobile={setWorkspacePopoverOpenMobile}
          onOpenWorkspaceSettings={onOpenWorkspaceSettings}
        />
      ) : user ? (
        <AppSidebarWorkspaceMenu
          expanded={expanded}
          open={workspaceMenuOpen}
          onOpenChange={setWorkspaceMenuOpen}
          currentWorkspace={currentWorkspace}
          workspaces={workspaces}
          workspaceRoles={workspaceRoles}
          workspacesLoading={workspacesLoading}
          handleWorkspaceChange={handleWorkspaceChange}
          isDesktop={isDesktop}
          setShowCreateForm={setShowCreateForm}
          setWorkspaceDrawerView={setWorkspaceDrawerView}
          setWorkspacePopoverOpenMobile={setWorkspacePopoverOpenMobile}
          onOpenWorkspaceSettings={onOpenWorkspaceSettings}
        />
      ) : (
        <AppSidebarWorkspaceMenu
          expanded={expanded}
          open={workspaceMenuOpen}
          onOpenChange={setWorkspaceMenuOpen}
          currentWorkspace={{ id: '', display_name: 'Acme, Inc.', avatar_path: null } as any}
          workspaces={[]}
          workspaceRoles={{}}
          workspacesLoading={false}
          handleWorkspaceChange={() => {}}
          isDesktop={isDesktop}
          setShowCreateForm={() => {}}
          setWorkspaceDrawerView={() => {}}
          setWorkspacePopoverOpenMobile={() => {}}
          onOpenWorkspaceSettings={() => {}}
        />
      )}

      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-hidden pb-2 px-2 pt-2">
        {/*
         * prefetch behavior keyed off `overlayMode`:
         *   - When the sidebar is mounted as the fullscreen-chat overlay
         *     (only path that hits this is /chat, where the @chat parallel
         *     slot is active), set prefetch={false} on every NavLink. RSC
         *     prefetches into a parallel-route active tree crash Next
         *     15.5.9's router with `TypeError: e is not iterable` in
         *     applyRouterStatePatchToTree. See vercel/next.js#72541.
         *     Clicking still works; we lose only the prefetch snappiness.
         *   - In the normal docked sidebar, primary nav items force
         *     prefetch={true} so the most-traveled paths feel instant.
         *   - Secondary items pass prefetch={undefined} — Next's default.
         *
         * Groups:
         *   (no label): Marketplace, Overview, Visualizer, Customers, Wallet
         *   CHANNELS:   Search, Social, TV/CTV, Programmatic, Audio, Retail
         *   TOOLS:      Budget, Settings, Alerts, Campaigns, Creatives, Properties
         *               (pinned subset + "More tools" popover for the rest)
         *
         * Removed nav links:
         *   - Activity (route still exists at /activity; surfaced as
         *     "Alerts" in the Automation group below).
         *   - Atrium group (Work / Admin) — moved into the user menu
         *     dropdown, still gated by !!email.endsWith('@atrium.run').
         *   - Brands — removed Sherman Phase 4 (Stephen 2026-05-19).
         */}

        <SidebarNavGroup expanded={expanded}>
          <NavLink
            href="/"
            variant="sidebar"
            iconOnly={!expanded}
            prefetch={overlayMode ? false : true}
            activeMatch={(pathname) =>
              pathname === '/' || pathname === '/marketplace' || pathname.startsWith('/marketplace/')
            }
            icon={<HugeiconsIcon icon={Home03StrokeIcon} />}
            activeIcon={<HugeiconsIcon icon={Home03SolidIcon} />}
          >
            Home
          </NavLink>
          <NavLink
            href="/overview"
            variant="sidebar"
            iconOnly={!expanded}
            prefetch={overlayMode ? false : true}
            activeMatch={(pathname) => pathname === '/overview' || pathname.startsWith('/overview/')}
            icon={<HugeiconsIcon icon={BinocularsStrokeIcon} />}
            activeIcon={<HugeiconsIcon icon={BinocularsSolidIcon} />}
          >
            Overview
          </NavLink>
          <NavLink
            href="/visualizer"
            variant="sidebar"
            iconOnly={!expanded}
            prefetch={overlayMode ? false : true}
            activeMatch={(pathname) =>
              pathname === '/visualizer' || pathname.startsWith('/visualizer/') || pathname === '/traffic' || pathname.startsWith('/traffic/')
            }
            icon={<HugeiconsIcon icon={Route01StrokeIcon} />}
            activeIcon={<HugeiconsIcon icon={Route01SolidIcon} />}
          >
            Visualizer
          </NavLink>
          <NavLink
            href="/customers"
            variant="sidebar"
            iconOnly={!expanded}
            prefetch={overlayMode ? false : true}
            activeMatch={(pathname) =>
              pathname === '/customers' || pathname.startsWith('/customers/')
            }
            icon={<HugeiconsIcon icon={UserDollarStrokeIcon} />}
            activeIcon={<HugeiconsIcon icon={UserDollarSolidIcon} />}
          >
            Customers
          </NavLink>
          <NavLink
            href="/wallet"
            variant="sidebar"
            iconOnly={!expanded}
            prefetch={overlayMode ? false : true}
            activeMatch={(pathname) => pathname === '/wallet' || pathname.startsWith('/wallet/')}
            icon={<HugeiconsIcon icon={Wallet01StrokeIcon} />}
            activeIcon={<HugeiconsIcon icon={Wallet01SolidIcon} />}
          >
            Wallet
          </NavLink>
        </SidebarNavGroup>

        <SidebarPinnedChannelsGroup
          expanded={expanded}
          overlayMode={overlayMode}
          channels={sidebarChannels}
        />

        <SidebarPinnedToolsGroup
          expanded={expanded}
          overlayMode={overlayMode}
          tools={sidebarTools}
          alertsBadge={alertsBadge}
        />

        {/* ── AUTOMATION (Subagents/Alerts) ──
            Stephen 2026-06-02: hidden from sidebar while we defer the
            Automations surfaces. The routes still exist as fixture
            pages (Harris PR #751); Commands, Scheduled, and Alerts live in Tools above.
            Un-comment this block when the Automations product spec
            (subagents schema + commands palette + scheduled wrapper +
            real alerts feed) is ready.
        <div className="flex flex-col gap-0.5">
          <div
            aria-hidden={!expanded || undefined}
            className={cn(
              'h-5 px-3 pb-0.5 pt-1 text-[11px] font-medium uppercase tracking-wider text-atrium-black/40 transition-opacity duration-200',
              expanded ? 'opacity-100' : 'opacity-0'
            )}
          >
            Automations
          </div>
          <NavLink
            href="/subagents"
            variant="sidebar"
            iconOnly={!expanded}
            prefetch={overlayMode ? false : true}
            activeMatch={(pathname) => pathname === '/subagents' || pathname.startsWith('/subagents/')}
            icon={<HugeiconsIcon icon={AiBrain05StrokeIcon} />}
            activeIcon={<HugeiconsIcon icon={AiBrain05SolidIcon} />}
          >
            Subagents
          </NavLink>
          <NavLink
            href="/alerts"
            variant="sidebar"
            iconOnly={!expanded}
            prefetch={overlayMode ? false : true}
            activeMatch={(pathname) => pathname === '/alerts' || pathname.startsWith('/alerts/')}
            icon={<HugeiconsIcon icon={Notification01StrokeIcon} />}
            activeIcon={<HugeiconsIcon icon={Notification01SolidIcon} />}
          >
            Alerts
          </NavLink>
        </div>
        */}

      </nav>

      <div className="shrink-0 px-2 pt-2">
        <NavLink
          href="/developers"
          variant="sidebar"
          iconOnly={!expanded}
          prefetch={overlayMode ? false : undefined}
          activeMatch={(pathname) =>
            pathname === '/developers' || pathname.startsWith('/developers/')
          }
          icon={<HugeiconsIcon icon={CodeCircleStrokeIcon} />}
          activeIcon={<HugeiconsIcon icon={CodeCircleSolidIcon} />}
        >
          Developers
        </NavLink>
      </div>
    </>
  )
}

export function AppSidebar({
  mode,
  initialRailMode,
}: {
  mode?: 'overlay'
  initialRailMode?: SidebarRailMode | null
}) {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [workspaceMenuOpen, setWorkspaceMenuOpenState] = useState(false)
  const [workspacePopoverOpenMobile, setWorkspacePopoverOpenMobile] = useState(false)
  const [workspaceDrawerView, setWorkspaceDrawerView] = useState<WorkspaceDrawerView>('list')
  const [workspaceSettingsOpen, setWorkspaceSettingsOpen] = useState(false)
  const [workspaceSettingsSection, setWorkspaceSettingsSection] = useState('general')
  const [railMode, setRailModeState] = useState<SidebarRailMode>(() => {
    if (mode === 'overlay') return 'expanded'
    return initialRailMode ?? 'collapsed'
  })
  const railRef = useRef<HTMLElement | null>(null)

  const { closeChat, registerAppRailCollapser, registerAppRailToggler, chatOpen, registerAppSidebarRailMode } = useChat()

  useEffect(() => {
    if (mode === 'overlay') return
    if (initialRailMode == null) {
      const storedMode = readSidebarRailMode()
      if (storedMode) setRailModeState(storedMode)
    }
  }, [initialRailMode, mode])

  const setRailMode = useCallback((newMode: SidebarRailMode) => {
    if (mode === 'overlay') return
    if (newMode === 'expanded') closeChat()
    setRailModeState(newMode)
    try {
      localStorage.setItem(SIDEBAR_RAIL_MODE_STORAGE_KEY, newMode)
      document.cookie = `${SIDEBAR_RAIL_MODE_COOKIE_KEY}=${encodeURIComponent(newMode)}; path=/; max-age=31536000; samesite=lax`
    } catch {
      /* ignore */
    }
  }, [closeChat, mode])

  const setWorkspaceMenuOpen = useCallback((open: boolean) => {
    setWorkspaceMenuOpenState(open)
  }, [])

  const toggleSidebarRailExpandedCollapsed = useCallback(() => {
    if (mode === 'overlay') return
    let willExpand = false
    setRailModeState((prev) => {
      const next: SidebarRailMode = prev === 'expanded' ? 'collapsed' : 'expanded'
      willExpand = next === 'expanded'
      try {
        localStorage.setItem(SIDEBAR_RAIL_MODE_STORAGE_KEY, next)
        document.cookie = `${SIDEBAR_RAIL_MODE_COOKIE_KEY}=${encodeURIComponent(next)}; path=/; max-age=31536000; samesite=lax`
      } catch {
        /* ignore */
      }
      return next
    })
    if (willExpand) closeChat()
  }, [closeChat, mode])

  // Keep the app rail collapsed while chat is open.
  useLayoutEffect(() => {
    if (mode === 'overlay') {
      setRailModeState('expanded')
      return
    }
    if (chatOpen) {
      setRailMode('collapsed')
    }
  }, [mode, chatOpen, setRailMode])

  useLayoutEffect(() => {
    if (mode === 'overlay') {
      registerAppRailCollapser(null)
      return
    }
    registerAppRailCollapser(() => {
      setRailMode('collapsed')
    })
    return () => { registerAppRailCollapser(null) }
  }, [mode, registerAppRailCollapser, setRailMode])

  useLayoutEffect(() => {
    if (mode === 'overlay') {
      registerAppRailToggler(null)
      return
    }
    registerAppRailToggler(toggleSidebarRailExpandedCollapsed)
    return () => { registerAppRailToggler(null) }
  }, [mode, registerAppRailToggler, toggleSidebarRailExpandedCollapsed])

  useLayoutEffect(() => {
    if (mode === 'overlay') {
      registerAppSidebarRailMode('expanded')
    } else {
      registerAppSidebarRailMode(railMode === 'collapsed' ? 'collapsed' : 'expanded')
    }
    return () => { registerAppSidebarRailMode(null) }
  }, [mode, railMode, registerAppSidebarRailMode])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      if (e.key !== 'j' && e.key !== 'J') return
      // Intentionally not skipping inputs: ⌘/Ctrl+J is a chord (won't type “j”) and should work from the chat prompt too.
      e.preventDefault()
      toggleSidebarRailExpandedCollapsed()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [toggleSidebarRailExpandedCollapsed])

  /** Expanded layout mode: wide rail. Collapsed mode: narrow rail (icon strip). */
  const railWide = railMode === 'expanded'

  /** When true, the rail takes real flex width so main content isn't overlapped. Collapsed mode keeps a narrow aside with the panel overlaying content at icon width. */
  const railUsesLayoutWidth = mode === 'overlay' || railMode === 'expanded'

  const { workspace: currentWorkspace, workspaces, workspaceRoles, isLoading: workspacesLoading, switchWorkspace } =
    useWorkspace()
  const { user, isLoading: userLoading } = useCachedUser()
  const isDesktop = useIsDesktop()

  const logoHref = '/'

  useEffect(() => {
    if (typeof window !== 'undefined') {
      ;(window as any).openWorkspaceSettings = (section?: string) => {
        setWorkspaceSettingsSection(section ?? 'credits')
        setWorkspaceSettingsOpen(true)
      }
    }
    return () => {
      if (typeof window !== 'undefined') {
        delete (window as any).openWorkspaceSettings
      }
    }
  }, [])

  const handleWorkspaceChange = useCallback(
    (orgId: string) => {
      switchWorkspace(orgId)
    },
    [switchWorkspace]
  )

  const handleCreateSuccess = useCallback(() => {
    setShowCreateForm(false)
    window.location.reload()
  }, [])

  /** Clicking empty chrome toggles expanded/collapsed (same as ⌘J); real controls keep default behavior. */
  const handleSidebarBackgroundClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (mode === 'overlay') return
      const el = e.target as HTMLElement
      if (
        el.closest('[aria-disabled="true"]') ||
        el.closest(
          [
            'a[href]',
            'button',
            'input',
            'textarea',
            'select',
            'label',
            '[contenteditable="true"]',
            '[role="button"]',
            '[role="menuitem"]',
            '[role="menuitemcheckbox"]',
            '[role="menuitemradio"]',
            '[role="option"]',
            '[role="checkbox"]',
            '[role="radio"]',
            '[role="switch"]',
            '[role="tab"]',
            '[role="combobox"]',
            '[role="dialog"]',
            '[role="listbox"]',
            '[data-radix-popper-content-wrapper]',
            '[data-radix-popover-content]',
          ].join(', ')
        )
      ) {
        return
      }
      toggleSidebarRailExpandedCollapsed()
    },
    [mode, toggleSidebarRailExpandedCollapsed]
  )

  return (
    <>
      <TooltipProvider delayDuration={0} skipDelayDuration={0}>
        <aside
          ref={railRef}
          data-app-sidebar
          className={cn(
            'group/sidebar relative z-[100] flex h-full shrink-0 flex-col overflow-visible bg-atrium-sidebar',
            mode !== 'overlay' && 'border-r border-atrium-border',
            mode === 'overlay'
              ? 'w-full'
              : 'transition-[width] duration-200 ease-out motion-reduce:transition-none',
            !railUsesLayoutWidth && 'w-14'
          )}
          style={mode !== 'overlay' && railUsesLayoutWidth ? { width: APP_SIDEBAR_EXPANDED_WIDTH } : undefined}
        >
          <PendingNavProvider>
            <div
              onClick={handleSidebarBackgroundClick}
              className={cn(
                'flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-atrium-sidebar pb-2',
                mode !== 'overlay' && (railWide ? 'cursor-w-resize' : 'cursor-e-resize'),
                mode !== 'overlay' && '[&_a]:cursor-inherit [&_button:not(:disabled)]:cursor-inherit',
                // railMode === 'expanded' ? 'rounded-xl border drop-shadow-sm/5 bg-atrium-sidebar' : 'border-r py-4 bg-atrium-background',
                railUsesLayoutWidth
                  ? 'relative z-0 w-full'
                  : cn(
                      // Stephen 2026-05-19: when collapsed, the inner absolute div
                      // paints over the outer <aside>'s border-r. Re-add the border
                      // here so the right edge stays visible in both states.
                      'absolute left-0 top-0 z-0 border-r border-atrium-border transition-[width] duration-200 ease-out motion-reduce:transition-none',
                      railWide ? 'w-[276px]' : 'w-14'
                    )
              )}
            >
              <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                <SidebarRailContent
                  expanded={railWide}
                  onOpenWorkspaceSettings={(section) => {
                    setWorkspaceSettingsSection(section ?? 'general')
                    setWorkspaceSettingsOpen(true)
                  }}
                  userLoading={userLoading}
                  user={user}
                  currentWorkspace={currentWorkspace}
                  workspaces={workspaces}
                  workspaceRoles={workspaceRoles}
                  workspacesLoading={workspacesLoading}
                  workspaceMenuOpen={workspaceMenuOpen}
                  setWorkspaceMenuOpen={setWorkspaceMenuOpen}
                  handleWorkspaceChange={handleWorkspaceChange}
                  isDesktop={isDesktop}
                  setShowCreateForm={setShowCreateForm}
                  setWorkspaceDrawerView={setWorkspaceDrawerView}
                  setWorkspacePopoverOpenMobile={setWorkspacePopoverOpenMobile}
                  logoHref={logoHref}
                  overlayMode={mode === 'overlay'}
                />
              </div>
            </div>
          </PendingNavProvider>
        </aside>
      </TooltipProvider>

      <SlideOutPanel
        open={workspacePopoverOpenMobile}
        onOpenChange={(open) => {
          setWorkspacePopoverOpenMobile(open)
          if (!open) setWorkspaceDrawerView('list')
        }}
        title={workspaceDrawerView === 'list' ? 'Workspace' : 'Create workspace'}
        direction="right"
        className="w-full lg:max-w-md z-[9999998]"
      >
        <div className="flex flex-col h-full text-atrium-black">
          {workspaceDrawerView === 'list' && (
            <div className="flex flex-col p-2">
              {workspacesLoading ? (
                <div className="flex flex-col gap-2 p-2">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-2 items-center px-3 py-2">
                      <Skeleton className="w-6 h-6 rounded-full bg-atrium-border/60" />
                      <Skeleton className="flex-1 h-4 bg-atrium-border/60" />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setWorkspaceDrawerView('create')}
                      className="font-normal text-accent-600"
                    >
                      <HugeiconsIcon icon={PlusSignStrokeIcon} className="size-3.5" />
                      Create workspace
                    </Button>
                    {currentWorkspace && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setWorkspacePopoverOpenMobile(false)
                          setWorkspaceSettingsSection('general')
                          setWorkspaceSettingsOpen(true)
                        }}
                        className="font-normal text-accent-600"
                      >
                        <HugeiconsIcon icon={SettingsSolidIcon} className="size-3.5" />
                        Workspace settings
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-col">
                    {workspaces.map((org) => (
                      <button
                        key={org.id}
                        type="button"
                        onClick={() => {
                          handleWorkspaceChange(org.id)
                          setWorkspacePopoverOpenMobile(false)
                        }}
                        className={cn(
                          'flex gap-2 items-center px-3 py-2.5 text-left text-sm font-normal rounded-lg transition-colors',
                          currentWorkspace?.id === org.id ? 'bg-atrium-content' : 'hover:bg-neutral-100'
                        )}
                      >
                        <Avatar className="size-8 rounded-full shrink-0">
                          <AvatarImage
                            src={
                              org.avatar_path
                                ? `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/workspace-avatars/${org.avatar_path}`
                                : undefined
                            }
                          />
                          <AvatarFallback className="rounded-full bg-accent-600 text-transparent">
                            {'\u200b'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="flex-1 truncate text-atrium-black">{org.display_name}</span>
                        {currentWorkspace?.id === org.id && (
                          <HugeiconsIcon icon={Tick02Icon} className="w-3.5 h-3.5 text-accent-600 shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {workspaceDrawerView === 'create' && (
            <div className="flex-1 overflow-y-auto min-h-0">
              <WorkspaceForm
                onSuccess={() => {
                  handleCreateSuccess()
                  setWorkspaceDrawerView('list')
                  setWorkspacePopoverOpenMobile(false)
                }}
                onCancel={() => setWorkspaceDrawerView('list')}
              />
            </div>
          )}
        </div>
      </SlideOutPanel>

      {isDesktop && (
        <SlideOutPanel
          open={showCreateForm}
          onOpenChange={setShowCreateForm}
          title="Create workspace"
          direction="right"
          className="w-full lg:max-w-md z-[9999999]"
        >
          <WorkspaceForm onSuccess={handleCreateSuccess} onCancel={() => setShowCreateForm(false)} />
        </SlideOutPanel>
      )}

      <SlideOutPanel
        open={workspaceSettingsOpen}
        onOpenChange={setWorkspaceSettingsOpen}
        title="Workspace Settings"
        direction="right"
        className="w-full lg:max-w-md z-[9999999]"
      >
        <WorkspaceSettingsPanel
          open={workspaceSettingsOpen}
          onOpenChange={setWorkspaceSettingsOpen}
          defaultOpenSection={workspaceSettingsSection}
        />
      </SlideOutPanel>

    </>
  )
}
